import type { AxiosError } from "axios";
import axios from "axios";
import type { ChangeEvent, ReactNode } from "react";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import useSWR from "swr";
import {
  PrimaryButtonMediumV2,
  SecondaryButtonMediumV2,
  SecondaryButtonSmallV2,
} from "../Buttons/Buttons";
import { CheckBoxNoLabel } from "../CheckBoxes/CheckBoxes";
import { GenericDialogBody } from "../ConfirmDialog/ConfirmDialog";
import { DelayedSpinner } from "../DelayedSpinner/DelayedSpinner";
import { CheckBoxContainer, CheckBoxFinePrintLabel } from "../Form/Form";
import {
  CSVIcon,
  DocumentIcon,
  DownloadIcon,
  LoadingIcon,
} from "../Icons/Icons";
import { useNotifications } from "../Notifications/NotificationsContext";
import { RadioButton } from "../RadioButton/RadioButton";
import {
  TextMdBold,
  TextMdRegular,
  TextXlBold,
} from "../Typography/TypographyV2";
import { endpoints } from "../../endpoints";
import { RadioButtonContainer } from "../../layout/FormLayout";
import { screenSize } from "../../theme";
import type { OptionType } from "../../types/types";
import type {
  AttributeTemplateSchema,
  ProductUploadStatus,
} from "../../types/types.PIM";
import { toTitleCase } from "../../util/util";
import { spacingTokens } from "../../designTokens";

const POLL_TIMEOUT = 3000;

const getTemplateFileDate = () => {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

// Shared with the CSV upload flow (UploadTemplateFiles / UploadEntityTemplateFiles),
// which import these from their respective createXFromUploads.util files.
export const DownloadTemplateContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  @media ${screenSize.medium} {
    width: 100%;
  }
`;

export const RegularTextBlock = styled(TextMdRegular).attrs({
  color: "secondary",
})`
  display: block;
`;

export const TemplateButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const DocumentAnchorCard = styled.a`
  text-decoration: none;
  cursor: pointer;
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  padding: 11px 15px;
  width: fit-content;
  color: ${({ theme }) => theme.primaryTextColor};
  :hover,
  :focus {
    border: ${({ theme }) => theme.resolve("border_2xl")} solid
      ${({ theme }) => theme.primaryBorder};
  }
`;

export const convertArrayBufferToZipUrl = (data: Blob) =>
  URL.createObjectURL(data);

export const DocumentDownloadContainer = ({
  link,
  documentName,
  type,
}: {
  link: string;
  documentName: string;
  type: "application/zip" | "text/csv";
}) => {
  const updatedDocumentName = (() => {
    const ext = documentName.split(".").pop();
    const possible_extensions = ["csv", "zip"];
    return possible_extensions.some((possible_ext) => possible_ext === ext)
      ? documentName
      : `${documentName}.${type.includes("csv") ? "csv" : "zip"}`;
  })();
  return (
    <DocumentAnchorCard
      href={link}
      download={updatedDocumentName}
      target="_blank"
      rel="noopener noreferrer"
      type={type}
    >
      {type === "application/zip" ? (
        <DocumentIcon width={24} height={24} />
      ) : (
        <CSVIcon width={24} height={24} />
      )}
      {updatedDocumentName}
      <DownloadIcon width={16} height={16} />
    </DocumentAnchorCard>
  );
};

const DocumentDownloadCard = styled.div`
  cursor: pointer;
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  padding: 11px 15px;
  width: fit-content;
  color: ${({ theme }) => theme.primaryTextColor};
  :hover,
  :focus {
    border: ${({ theme }) => theme.resolve("border_2xl")} solid
      ${({ theme }) => theme.primaryBorder};
  }
`;

const PrimaryButtonFlex = styled(PrimaryButtonMediumV2)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_3xl")};
  width: fit-content;
`;

export type TemplateExportDocument = { id: string; name: string; url: string };

export type TemplateFilesDownloadPanelCopy = {
  description: string;
  reDownloadNote: string;
  includeExistingDataLabel: string;
  groupsSectionNote: string;
  collectionsSectionNote: string;
  preparingFilesMessage: string;
  pollFailedMessage: string;
  genericExportError: string;
};

export type TemplateFilesDownloadPanelProps = {
  template: OptionType<string>;
  tenantId: string;
  /** An export already in progress/complete, restored when navigating back to this step. */
  exportId?: string;
  defaultSelectedItems?: string[] | "all";
  copy: TemplateFilesDownloadPanelCopy;
  onSetExportId: (exportId?: string) => void;
  onSetSelectedItems: (items: string[] | "all") => void;
  onMoveBack: () => void;
  onContinueFromDownload: (collectionFileNames: Record<string, string>) => void;
  /** Kicks off the async export for the given selection and returns the export job id. */
  startExport: (selectedItems: string[] | "all") => Promise<{ id: string }>;
  /** Return a user-facing message when `error` means "no items match this template" (else undefined). */
  getNoItemsErrorMessage?: (error: AxiosError) => string | undefined;
  pollExportStatus: (exportId: string) => Promise<{
    status: ProductUploadStatus;
    documents: TemplateExportDocument[];
  }>;
  renderSpecificItemsSelector: (props: {
    handleSelectRows: (rows: string[] | "all" | undefined) => void;
    selectedTemplate: OptionType<string>;
    defaultSelectedRows: string[] | "all" | undefined;
  }) => ReactNode;
};

/**
 * Download-template-files step of the product/entity CSV bulk upload flow.
 * Shared between CreatePimProductFromUploads and CreateEntityFromUploads: both let
 * the user download blank templates, or (via `renderSpecificItemsSelector`) generate
 * an async export prefilled with all or a specific selection of existing records.
 */
export const TemplateFilesDownloadPanel = ({
  template,
  tenantId,
  exportId: initialExportId,
  defaultSelectedItems,
  copy,
  onSetExportId,
  onSetSelectedItems,
  onMoveBack,
  onContinueFromDownload,
  startExport,
  getNoItemsErrorMessage,
  pollExportStatus,
  renderSpecificItemsSelector,
}: TemplateFilesDownloadPanelProps) => {
  const [poll_timeout_id, set_poll_timeout_id] = useState<NodeJS.Timeout>();
  const [export_id, set_export_id] =
    useState<string | undefined>(initialExportId);
  const [item_option, set_item_option] = useState<"all" | "specific">(
    defaultSelectedItems ? "specific" : "all"
  );
  const [show_item_option, set_show_item_option] = useState(false);
  const [selected_items, set_selected_items] = useState<string[] | "all">(
    defaultSelectedItems ?? []
  );
  const [document_urls, set_document_urls] = useState<TemplateExportDocument[]>(
    []
  );
  const [is_preparing_files, set_is_preparing_files] = useState(false);

  const { t } = useTranslation();
  const theme = useTheme();
  const { notifyError } = useNotifications();

  const { data: template_schema, error: template_schema_error } = useSWR<
    AttributeTemplateSchema,
    AxiosError
  >(`/v2/tenants/${tenantId}/pim/templates/${template.value}`);

  const reset_export_id = useCallback(() => {
    set_export_id(undefined);
    onSetExportId(undefined);
  }, [onSetExportId]);

  const reset_export_selection = useCallback(() => {
    set_show_item_option(false);
    set_item_option("all");
    reset_export_id();
  }, [reset_export_id]);

  const onDownload = async () => {
    try {
      const { id } = await startExport(selected_items);
      set_export_id(id);
      onSetExportId(id);
    } catch (error) {
      const noItemsMessage = getNoItemsErrorMessage?.(error as AxiosError);
      if (noItemsMessage) {
        notifyError(noItemsMessage);
        reset_export_selection();
      } else {
        notifyError(copy.genericExportError);
      }
    }
  };

  const onCheck = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.currentTarget.checked;
    set_show_item_option(val);
    if (!val) {
      reset_export_selection();
      clearTimeout(poll_timeout_id);
    }
  };

  const on_radio_button_click = (option: "all" | "specific") => {
    set_item_option(option);
    reset_export_id();
    set_document_urls([]);
  };

  const handleSelectRows = (rows: string[] | "all" | undefined) => {
    if (rows === undefined) return;
    set_selected_items(rows);
    onSetSelectedItems(rows);
  };

  const onAsyncStatusFailed = useCallback(() => {
    notifyError(copy.pollFailedMessage);
    reset_export_selection();
  }, [copy.pollFailedMessage, notifyError, reset_export_selection]);

  useEffect(() => {
    if (initialExportId) {
      set_export_id(initialExportId);
      set_show_item_option(true);
    }
  }, [initialExportId]);

  useEffect(() => {
    if (defaultSelectedItems) {
      set_selected_items(defaultSelectedItems);
      set_item_option("specific");
    }
  }, [defaultSelectedItems]);

  useEffect(() => {
    let timeout_id: NodeJS.Timeout;
    const pollFn = (current_export_id: string) => {
      timeout_id = setTimeout(() => {
        const polling = async () => {
          try {
            const { status, documents } = await pollExportStatus(
              current_export_id
            );
            switch (status) {
              case "in_progress":
                timeout_id = setTimeout(polling, POLL_TIMEOUT);
                set_poll_timeout_id(timeout_id);
                break;
              case "failed":
                clearTimeout(timeout_id);
                set_document_urls([]);
                set_is_preparing_files(false);
                onAsyncStatusFailed();
                break;
              case "successful":
                clearTimeout(timeout_id);
                set_document_urls(documents);
                set_is_preparing_files(false);
                reset_export_id();
                break;
            }
          } catch {
            clearTimeout(timeout_id);
            set_document_urls([]);
            set_is_preparing_files(false);
            reset_export_id();
          }
        };
        polling();
      }, POLL_TIMEOUT);
      set_poll_timeout_id(timeout_id);
    };
    if (export_id) {
      set_is_preparing_files(true);
      pollFn(export_id);
    }
    return () => {
      clearTimeout(timeout_id);
    };
  }, [export_id, onAsyncStatusFailed, pollExportStatus, reset_export_id]);

  const buildCollectionFileNames = useCallback(() => {
    if (!template_schema) return {};
    return template_schema.collections.reduce<Record<string, string>>(
      (acc, { id, name }) => {
        const updated_name = `${template_schema.template_name.toLowerCase()}_${name.toLowerCase()}_${getTemplateFileDate()}`;
        acc[updated_name] = id;
        return acc;
      },
      {}
    );
  }, [template_schema]);

  const handleDownload = async (
    file: OptionType<string>,
    type: "application/zip" | "text/csv"
  ) => {
    const { data } = await axios.get<Blob>(file.value.replace("/api", ""), {
      responseType: "blob",
    });
    const url = convertArrayBufferToZipUrl(data);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download =
      file.label + (type === "application/zip" ? ".zip" : ".csv");
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const isLoading = !template_schema && !template_schema_error;

  if (isLoading) {
    return <DelayedSpinner />;
  }

  if (!template_schema) {
    return null;
  }

  const modifiedTemplate = template_schema.template_name.toLowerCase();
  const templateFileDate = getTemplateFileDate();

  const allFileUrl: OptionType<string> = {
    label: modifiedTemplate,
    value: `/api${endpoints.v2_tenants_tenant_id_templates_template_id_download(
      tenantId,
      template_schema.template_id
    )}?type=all`,
  };

  const groupFileUrl: OptionType<string> = {
    label: `${modifiedTemplate}_groups_${templateFileDate}`,
    value: `/api${endpoints.v2_tenants_tenant_id_templates_template_id_download(
      tenantId,
      template_schema.template_id
    )}?type=group`,
  };

  const collectionFilesUrl: OptionType<{ id: string; link: string }>[] =
    template_schema.collections.map(({ id, name }) => ({
      label: `${modifiedTemplate}_${name.toLowerCase()}_${templateFileDate}`,
      value: {
        id,
        link: `/api${endpoints.v2_tenants_tenant_id_templates_template_id_download(
          tenantId,
          template_schema.template_id
        )}?collection_id=${id}`,
      },
    }));

  const onContinue = () => onContinueFromDownload(buildCollectionFileNames());

  const renderSyncSection = () => {
    const isContinueButtonDisabled = !(
      (allFileUrl || groupFileUrl) &&
      Boolean(collectionFilesUrl)
    );

    return (
      <>
        <PrimaryButtonFlex
          onClick={() => handleDownload(allFileUrl, "application/zip")}
        >
          <DownloadIcon width={16} height={16} />
          {t("Download all files")}
        </PrimaryButtonFlex>
        <TextMdBold style={{ margin: 0 }}>{t("Groups")}</TextMdBold>
        <RegularTextBlock>{copy.groupsSectionNote}</RegularTextBlock>
        <div
          style={{
            margin: `${theme.resolve("spacing_md")} 0 ${theme.resolve(
              "spacing_3xl"
            )}`,
          }}
        >
          <DocumentDownloadCard
            onClick={() => handleDownload(groupFileUrl, "text/csv")}
          >
            <CSVIcon width={24} height={24} />
            {groupFileUrl.label}.csv
            <DownloadIcon width={16} height={16} />
          </DocumentDownloadCard>
        </div>
        {collectionFilesUrl.length > 0 && (
          <>
            <TextMdBold style={{ margin: 0 }}>{t("Collections")}</TextMdBold>
            <RegularTextBlock
              style={{ marginBottom: theme.resolve("spacing_md") }}
            >
              {copy.collectionsSectionNote}
            </RegularTextBlock>
            {collectionFilesUrl.map(({ label, value: { link } }, index) => (
              <div
                key={label + link}
                style={{
                  marginBottom:
                    index === collectionFilesUrl.length - 1
                      ? theme.resolve("spacing_3xl")
                      : theme.resolve("spacing_md"),
                }}
              >
                <DocumentDownloadCard
                  onClick={() =>
                    handleDownload({ label, value: link }, "text/csv")
                  }
                >
                  <CSVIcon width={24} height={24} />
                  {label}.csv
                </DocumentDownloadCard>
              </div>
            ))}
          </>
        )}
        <TemplateButtonContainer>
          <SecondaryButtonMediumV2 onClick={onMoveBack}>
            {t("Back")}
          </SecondaryButtonMediumV2>
          <PrimaryButtonMediumV2
            disabled={isContinueButtonDisabled}
            onClick={onContinue}
          >
            {t("Continue")}
          </PrimaryButtonMediumV2>
        </TemplateButtonContainer>
      </>
    );
  };

  const renderItemOptionRadios = () => (
    <RadioButtonContainer style={{ marginBottom: "15px" }}>
      <RadioButton
        name="items_option"
        value="all"
        handleChange={() => on_radio_button_click("all")}
        checked={item_option === "all"}
        optionTitle={t("All")}
      />
      <RadioButton
        name="items_option"
        value="specific"
        handleChange={() => on_radio_button_click("specific")}
        checked={item_option === "specific"}
        optionTitle={t("Specific")}
      />
    </RadioButtonContainer>
  );

  // Download button, prepared-document list and nav buttons for the async
  // flow. Kept separate from the (full-width) specific-items table so it can
  // stay inside the narrow DownloadTemplateContainer like the rest of the panel.
  const renderAsyncDownloadAndDocuments = () => {
    const isContinueButtonDisabled = !document_urls.length;

    return (
      <>
        <div style={{ margin: "15px 0" }}>
          <SecondaryButtonSmallV2
            onClick={onDownload}
            disabled={
              item_option === "specific" &&
              (selected_items === "all" || !selected_items.length)
            }
          >
            {t("Download")}
          </SecondaryButtonSmallV2>
        </div>
        {document_urls.map((doc) => (
          <Fragment key={doc.id}>
            <TextMdBold style={{ margin: 0 }}>
              {toTitleCase(doc.name)}
            </TextMdBold>
            <div
              style={{
                margin: `${theme.resolve("spacing_md")} 0 ${theme.resolve(
                  "spacing_3xl"
                )}`,
              }}
            >
              <DocumentDownloadContainer
                type="application/zip"
                link={doc.url}
                documentName={doc.name}
              />
            </div>
          </Fragment>
        ))}
        <TemplateButtonContainer style={{ marginTop: "30px" }}>
          <SecondaryButtonMediumV2 onClick={onMoveBack}>
            {t("Back")}
          </SecondaryButtonMediumV2>
          <PrimaryButtonMediumV2
            disabled={isContinueButtonDisabled}
            onClick={onContinue}
          >
            {t("Continue")}
          </PrimaryButtonMediumV2>
        </TemplateButtonContainer>
        <GenericDialogBody
          show={is_preparing_files}
          closeDialog={() => set_is_preparing_files(false)}
        >
          <div
            style={{
              padding: theme.resolve("spacing_xl"),
              display: "flex",
              gap: theme.resolve("spacing_md"),
              alignItems: "center",
            }}
          >
            <LoadingIcon
              width={15}
              height={15}
              fill={theme.primaryButtonTextColor}
            />
            <TextMdRegular>{copy.preparingFilesMessage}</TextMdRegular>
          </div>
        </GenericDialogBody>
      </>
    );
  };

  return (
    <>
      <DownloadTemplateContainer>
        <TextXlBold style={{ margin: `0 0 ${theme.resolve("spacing_xs")}` }}>
          {t("Download Template files")}
        </TextXlBold>
        <TextMdRegular
          color="secondary"
          as="p"
          style={{ marginBottom: theme.resolve("spacing_md") }}
        >
          {copy.description}
        </TextMdRegular>
        <TextMdRegular
          color="secondary"
          as="p"
          style={{ marginBottom: theme.resolve("spacing_md") }}
        >
          {copy.reDownloadNote}
        </TextMdRegular>
        <CheckBoxContainer style={{ marginTop: "15px", marginBottom: "15px" }}>
          <div style={{ width: "22px", marginRight: spacingTokens.spacing_xs }}>
            <CheckBoxNoLabel
              name="include_existing_data"
              id="include_existing_data"
              checked={show_item_option}
              onChange={onCheck}
            />
          </div>
          <CheckBoxFinePrintLabel htmlFor="include_existing_data">
            <span>{copy.includeExistingDataLabel}</span>
          </CheckBoxFinePrintLabel>
        </CheckBoxContainer>
        {show_item_option ? renderItemOptionRadios() : renderSyncSection()}
      </DownloadTemplateContainer>
      {/* Rendered outside DownloadTemplateContainer so the picker table can use
          the full page width instead of being capped at 50%. */}
      {show_item_option &&
        item_option === "specific" &&
        renderSpecificItemsSelector({
          handleSelectRows,
          selectedTemplate: template,
          defaultSelectedRows: defaultSelectedItems,
        })}
      {show_item_option && (
        <DownloadTemplateContainer>
          {renderAsyncDownloadAndDocuments()}
        </DownloadTemplateContainer>
      )}
    </>
  );
};
